import { useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import Auth from "../../Auth";
import Onboarding from "./Onboarding";
import { Container } from "./styles";
import UserData from "../../models/UserData";
import { useAppStore } from "../../stores/appStore";
import useInstalledPlugins from "../../hooks/useInstalledPlugins";
import InlineSVG from "react-inlinesvg";
import { useToast } from "../ToastWrapper";
import { AppEventPublisher } from "../../AppEventPublisher";

export default function InstallButton({
  plugin,
  setOnboardingState,
  buttonBackgroundColor,
  buttonForegroundColor,
  titleCase = false,
  openLogin,
  suppressRemove = false,
  showPlus = false,
  showInstall = false,
  isOnboardingFlow = false,
}) {
  const [installing, setInstalling] = useState(false);
  const [uninstalling, setUninstalling] = useState(false);
  const [installation, setInstallation] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { setToast } = useToast();

  const { installedPlugins, refetchInstallations } = useInstalledPlugins();

  const user = useAppStore((state) => state.user);

  const findInstallation = () => {
    const installation = installedPlugins.find((installation) => plugin.id === installation.plugin.id);
    setInstallation(installation || false);
  };

  const install = async () => {
    setInstalling(true);

    let success = false;

    try {
      await Auth.fetchWithIdToken(`/api/installations`, {
        method: "POST",
        pluginId: plugin.id,
      });
      success = true;
    } catch (e) {
      const message = (await e.response.json()).message;
      if (message === "The following fields must be unique: user_id,plugin_id") {
        setToast("This plugin is already installed!");
      } else {
        Sentry.captureException(e);
        setToast("Error installing plugin: " + message);
      }
    }

    await refetchInstallations();
    setInstalling(false);

    if (success && isOnboardingFlow) {
      AppEventPublisher.onboarding.pluginInstalled(plugin.name);
    }

    if (success && !isOnboardingFlow) {
      const userData = new UserData(user.uid);
      await userData.fetch();

      if (userData.data.showPluginOnboarding) {
        setShowOnboarding(true);
        await userData.update({ showPluginOnboarding: false });
      }
    }
  };

  useEffect(() => {
    setOnboardingState?.(showOnboarding);
  }, [showOnboarding]);

  const uninstall = async () => {
    if (!installation) {
      setToast("Cannot uninstall a plugin you haven't installed");
      return;
    }

    setUninstalling(true);

    await Auth.fetchWithIdToken(`/api/installations/${installation.id}`, {
      method: "DELETE",
    });

    await refetchInstallations();
    setUninstalling(false);

    if (isOnboardingFlow) {
      AppEventPublisher.onboarding.pluginUninstalled(plugin.name);
    }
  };

  useEffect(() => {
    if (installedPlugins) {
      findInstallation();
    }
  }, [installedPlugins]);

  let buttonMode;

  if (installing) {
    buttonMode = "installing";
  } else if (uninstalling) {
    buttonMode = "uninstalling";
  } else if (installation) {
    buttonMode = "uninstall";
  } else {
    buttonMode = "install";
  }

  const buttonStyles = {
    backgroundColor: buttonBackgroundColor,
    color: buttonForegroundColor,
  };

  let addText;
  if (titleCase) {
    addText = "Add";
  } else {
    addText = "ADD";
  }

  if (showPlus) {
    addText = "+ " + addText;
  }

  let addTextIcon = null;
  if (showInstall) {
    addTextIcon = <InlineSVG src="/assets/import.svg" />;
  }

  return (
    <Container>
      {showOnboarding && (
        <Onboarding
          dismiss={() => setShowOnboarding(false)}
          plugin={plugin}
        />
      )}

      {buttonMode === "install" && (
        <button
          className="add"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (user) {
              install();
            } else {
              openLogin();
            }
          }}
          style={buttonStyles}
        >
          {addTextIcon}
          {addText}
        </button>
      )}

      {buttonMode === "uninstall" && !suppressRemove && (
        <button
          className="remove"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            uninstall();
          }}
          style={buttonStyles}
        >
          {titleCase ? "Remove" : "REMOVE"}
        </button>
      )}

      {buttonMode === "installing" && (
        <button
          style={buttonStyles}
          className="add"
        >
          {titleCase ? "Adding..." : "ADDING..."}
        </button>
      )}

      {buttonMode === "uninstalling" && (
        <button
          style={buttonStyles}
          className="remove"
        >
          {titleCase ? "Removing..." : "REMOVING..."}
        </button>
      )}
    </Container>
  );
}
